import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { gsap } from "gsap";
import {
  Mail, MapPin, Clock, MessageCircle, Calendar,
  ArrowRight, Check, RotateCcw, ChevronDown,
} from "lucide-react";

const BOOK_CALL_URL = "https://calendar.app.google/H45YrLnXhvd4TGEK6";

function BookCallButton({ fullWidth = false }) {
  return (
    <a
      href={BOOK_CALL_URL}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: "none", display: "block", width: fullWidth ? "100%" : "auto" }}
    >
      <button
        style={{
          width: fullWidth ? "100%" : "auto",
          padding: "13px 24px",
          background: "linear-gradient(135deg, #3a7a77 0%, #2D5C5A 100%)",
          border: "1px solid rgba(91,148,144,0.55)",
          borderRadius: 8,
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 500,
          fontSize: 15,
          color: "#fff",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 9,
          boxShadow: "0 0 22px rgba(91,148,144,0.32), 0 4px 14px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.14)",
          transition: "all 0.2s ease",
          letterSpacing: "-0.01em",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "linear-gradient(135deg, #479a96 0%, #3a7a77 100%)";
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 0 32px rgba(91,148,144,0.48), 0 6px 20px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.18)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "linear-gradient(135deg, #3a7a77 0%, #2D5C5A 100%)";
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 0 22px rgba(91,148,144,0.32), 0 4px 14px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.14)";
        }}
      >
        <Calendar size={15} strokeWidth={1.75} />
        Book a 30-min intro call
      </button>
    </a>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "AI / ML Product",
  "Web Application",
  "Mobile Application",
  "Cloud Infrastructure",
  "API / Integration",
  "Strategy & Consulting",
  "Other",
];

const inputBase = {
  width: "100%",
  background: "rgba(255,255,255,0.035)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8,
  padding: "13px 16px",
  fontSize: 15,
  fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  color: "#fff",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color .2s cubic-bezier(.4,0,.2,1), box-shadow .2s cubic-bezier(.4,0,.2,1), background .2s cubic-bezier(.4,0,.2,1)",
};

function FieldInput({ name, type = "text", placeholder, value, onChange, error }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      name={name}
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        ...inputBase,
        borderColor: error ? "#EF4444" : focused ? "#5B9490" : "rgba(255,255,255,0.12)",
        background: focused ? "rgba(91,148,144,0.06)" : "rgba(255,255,255,0.035)",
        boxShadow: focused ? "0 0 0 3px rgba(91,148,144,0.18)" : "none",
      }}
    />
  );
}

function FieldTextarea({ name, placeholder, value, onChange, error }) {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        ...inputBase,
        minHeight: 130,
        resize: "vertical",
        lineHeight: 1.55,
        borderColor: error ? "#EF4444" : focused ? "#5B9490" : "rgba(255,255,255,0.12)",
        background: focused ? "rgba(91,148,144,0.06)" : "rgba(255,255,255,0.035)",
        boxShadow: focused ? "0 0 0 3px rgba(91,148,144,0.18)" : "none",
      }}
    />
  );
}

function FieldSelect({ name, value, onChange }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <select
        name={name}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          ...inputBase,
          appearance: "none",
          WebkitAppearance: "none",
          cursor: "pointer",
          paddingRight: 40,
          borderColor: focused ? "#5B9490" : "rgba(255,255,255,0.12)",
          background: focused ? "rgba(91,148,144,0.06)" : "rgba(255,255,255,0.035)",
          boxShadow: focused ? "0 0 0 3px rgba(91,148,144,0.18)" : "none",
        }}
      >
        <option value="" disabled style={{ background: "#0c1413" }}>Select a type…</option>
        {PROJECT_TYPES.map((t) => (
          <option key={t} value={t} style={{ background: "#0c1413" }}>{t}</option>
        ))}
      </select>
      <span style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", display: "flex" }}>
        <ChevronDown size={18} strokeWidth={1.75} color="rgba(255,255,255,0.5)" />
      </span>
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <label style={{
      display: "block",
      fontFamily: "Montserrat, sans-serif",
      fontSize: 13,
      fontWeight: 500,
      color: "rgba(255,255,255,0.72)",
      letterSpacing: ".01em",
      marginBottom: 8,
    }}>
      {children}
    </label>
  );
}

function ContactChannel({ icon: Icon, label, value, href }) {
  const [hovered, setHovered] = useState(false);
  const inner = (
    <div
      style={{ display: "flex", gap: 16, alignItems: "flex-start" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span style={{
        flexShrink: 0,
        marginTop: 3,
        width: 38,
        height: 38,
        borderRadius: 9,
        display: "grid",
        placeItems: "center",
        background: "rgba(91,148,144,0.10)",
        border: "1px solid rgba(91,148,144,0.28)",
      }}>
        <Icon size={17} strokeWidth={1.6} color="#7BB5B1" />
      </span>
      <div>
        <div style={{
          fontFamily: "Montserrat, sans-serif",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: ".16em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.45)",
          marginBottom: 6,
        }}>
          {label}
        </div>
        <div style={{
          fontFamily: "Montserrat, sans-serif",
          fontSize: 18,
          fontWeight: 300,
          letterSpacing: "-0.01em",
          color: href ? (hovered ? "#9ccbc7" : "#7BB5B1") : "#fff",
          transition: "color .2s",
        }}>
          {value}
        </div>
      </div>
    </div>
  );
  if (href) {
    return (
      <a href={href} style={{ textDecoration: "none", display: "block" }}>
        {inner}
      </a>
    );
  }
  return inner;
}

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    projectType: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [visible, setVisible] = useState(false);
  const submittedName = useRef("");

  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (prefersReduced) { setVisible(true); return; }
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, [prefersReduced]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: null });
    }
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = "Name is required";
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email))
      e.email = "Valid email required";
    if (!formData.message.trim()) e.message = "Tell us a bit about your project";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const response = await axios.post(`${API}/leads`, formData);
      if (response.data.success) {
        submittedName.current = formData.name;
        setIsSubmitted(true);
        setFormData({ name: "", email: "", company: "", projectType: "", message: "" });
      } else {
        setSubmitError("Failed to submit. Please try again.");
      }
    } catch {
      setSubmitError("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeUp = prefersReduced ? {} : {
    opacity: visible ? 1 : 0,
    transform: visible ? "none" : "translateY(16px)",
    transition: "opacity .7s cubic-bezier(.25,.46,.45,.94), transform .7s cubic-bezier(.25,.46,.45,.94)",
  };

  const fadeUpDelayed = prefersReduced ? {} : {
    opacity: visible ? 1 : 0,
    transform: visible ? "none" : "translateY(16px)",
    transition: "opacity .7s .12s cubic-bezier(.25,.46,.45,.94), transform .7s .12s cubic-bezier(.25,.46,.45,.94)",
  };

  const [submitHovered, setSubmitHovered] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    if (prefersReduced || !headerRef.current) return;
    const el = headerRef.current;
    const children = Array.from(el.children);
    gsap.set(children, { opacity: 0, y: 28 });
    let tween;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          tween = gsap.to(children, {
            opacity: 1, y: 0,
            stagger: 0.13, duration: 0.78, ease: "power3.out",
          });
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      if (tween) tween.kill();
      gsap.set(children, { clearProps: "opacity,y" });
    };
  }, [prefersReduced]);

  return (
    <section
      id="contact"
      style={{
        position: "relative",
        padding: "40px 0 110px",
        background: "linear-gradient(to bottom, #060707 0%, rgba(6,7,7,0) 160px), #000",
      }}
    >
      <style>{`
        #contact input::placeholder,
        #contact textarea::placeholder { color: rgba(255,255,255,0.38); }
        @media (max-width: 900px) {
          .contact-main-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* Teal radial glow backdrop */}
      <div style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        background:
          "radial-gradient(circle at 72% 42%, rgba(91,148,144,0.16), transparent 42%)," +
          "radial-gradient(circle at 22% 88%, rgba(45,92,90,0.13), transparent 46%)," +
          "radial-gradient(circle at 50% -8%, rgba(45,92,90,0.10), transparent 40%)",
      }} />

      <div style={{
        maxWidth: 1280,
        margin: "0 auto",
        padding: "0 clamp(20px, 4vw, 56px)",
        position: "relative",
        zIndex: 1,
      }}>
        {/* Header */}
        <div ref={headerRef} style={{ maxWidth: 560 }}>
          <h2 style={{
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 300,
            fontSize: "clamp(36px, 4.4vw, 60px)",
            lineHeight: 1.02,
            letterSpacing: "-0.025em",
            color: "#fff",
            margin: 0,
          }}>
            Let's build <span style={{ color: "#7BB5B1" }}>together</span>
          </h2>
          <p className="contact-subtitle" style={{
            fontSize: 18,
            fontWeight: 300,
            lineHeight: 1.6,
            color: "rgba(255,255,255,0.6)",
            margin: "20px 0 0",
          }}>
            Tell us what you're working on. We'll get back to you within one business day.
          </p>
        </div>

        {/* Two-column grid */}
        <div
          className="contact-main-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "0.82fr 1fr",
            gap: "clamp(32px, 5vw, 80px)",
            alignItems: "center",
            marginTop: 56,
          }}
        >
          {/* Left — channels (hidden on mobile via CSS) */}
          <div className="contact-channels-col" style={{ position: "relative", ...fadeUp }}>
<div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 30 }}>
              <ContactChannel icon={Mail} label="Email" value="hello@grassurntech.com" href="mailto:hello@grassurntech.com" />
              <ContactChannel icon={MapPin} label="Studio" value="Toronto, Canada" />
              <ContactChannel icon={Clock} label="Response Time" value="Within one business day" />
              <div style={{ height: 1, background: "rgba(255,255,255,0.08)", margin: "4px 0" }} />
              <div>
                <div style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: ".16em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.45)",
                  marginBottom: 14,
                }}>
                  Prefer to chat?
                </div>
                <BookCallButton />
              </div>
            </div>
          </div>

          {/* Right — frosted-glass form */}
          <div style={fadeUpDelayed}>
            {/* Mobile-only: Book call button above form */}
            <div className="contact-book-mobile" style={{ marginBottom: 24 }}>
              <BookCallButton fullWidth />
            </div>
            <div style={{
              background: "linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.015))",
              border: "1px solid rgba(91,148,144,0.20)",
              borderRadius: 16,
              padding: "clamp(26px, 3vw, 40px)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              boxShadow: "0 24px 60px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.05)",
            }}>
              {isSubmitted ? (
                /* Success state */
                <div style={{ textAlign: "center", padding: "44px 8px" }}>
                  <span style={{
                    display: "inline-grid",
                    placeItems: "center",
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: "rgba(91,148,144,0.14)",
                    border: "1px solid rgba(91,148,144,0.35)",
                    marginBottom: 20,
                  }}>
                    <Check size={26} strokeWidth={2} color="#7BB5B1" />
                  </span>
                  <h3 style={{
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: 24,
                    fontWeight: 300,
                    letterSpacing: "-0.025em",
                    color: "#fff",
                    margin: 0,
                  }}>
                    Message sent
                  </h3>
                  <p style={{ fontSize: 15, color: "rgba(255,255,255,0.55)", margin: "12px 0 24px" }}>
                    Thanks, {submittedName.current || "there"} — we'll be in touch within one business day.
                  </p>
                  <button
                    onClick={() => {
                      setIsSubmitted(false);
                      setFormData({ name: "", email: "", company: "", projectType: "", message: "" });
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: 15,
                      fontWeight: 500,
                      color: "#7BB5B1",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: 0,
                      transition: "color .2s",
                    }}
                  >
                    Send another <RotateCcw size={15} strokeWidth={2} />
                  </button>
                </div>
              ) : (
                /* Form */
                <form onSubmit={handleSubmit}>
                  {/* Row 1 */}
                  <div className="contact-form-row-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                    <div>
                      <FieldLabel>Name *</FieldLabel>
                      <FieldInput name="name" placeholder="Your name" value={formData.name} onChange={handleChange} error={errors.name} />
                      {errors.name && <span style={{ color: "#EF4444", fontSize: 11, marginTop: 4, display: "block" }}>{errors.name}</span>}
                    </div>
                    <div>
                      <FieldLabel>Email *</FieldLabel>
                      <FieldInput name="email" type="email" placeholder="you@company.com" value={formData.email} onChange={handleChange} error={errors.email} />
                      {errors.email && <span style={{ color: "#EF4444", fontSize: 11, marginTop: 4, display: "block" }}>{errors.email}</span>}
                    </div>
                  </div>

                  {/* Row 2 */}
                  <div className="contact-form-row-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 20 }}>
                    <div>
                      <FieldLabel>Company</FieldLabel>
                      <FieldInput name="company" placeholder="Optional" value={formData.company} onChange={handleChange} />
                    </div>
                    <div>
                      <FieldLabel>Project Type</FieldLabel>
                      <FieldSelect name="projectType" value={formData.projectType} onChange={handleChange} />
                    </div>
                  </div>

                  {/* Message */}
                  <div style={{ marginTop: 20 }}>
                    <FieldLabel>Message *</FieldLabel>
                    <FieldTextarea name="message" placeholder="Tell us about your project…" value={formData.message} onChange={handleChange} error={errors.message} />
                    {errors.message && <span style={{ color: "#EF4444", fontSize: 11, marginTop: 4, display: "block" }}>{errors.message}</span>}
                  </div>

                  {submitError && (
                    <p style={{ color: "#EF4444", fontSize: 13, margin: "12px 0 0" }}>{submitError}</p>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    onMouseEnter={() => setSubmitHovered(true)}
                    onMouseLeave={() => setSubmitHovered(false)}
                    style={{
                      width: "100%",
                      background: isSubmitting
                        ? "rgba(45,92,90,0.45)"
                        : submitHovered
                          ? "linear-gradient(135deg, #479a96 0%, #3a7a77 100%)"
                          : "linear-gradient(135deg, #3a7a77 0%, #2D5C5A 100%)",
                      color: "#fff",
                      border: "1px solid rgba(91,148,144,0.52)",
                      borderRadius: 8,
                      padding: 17,
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: 15,
                      fontWeight: 500,
                      letterSpacing: ".01em",
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 10,
                      marginTop: 26,
                      transform: !isSubmitting && submitHovered ? "translateY(-2px)" : "none",
                      boxShadow: isSubmitting
                        ? "none"
                        : submitHovered
                          ? "0 0 28px rgba(91,148,144,0.45), 0 6px 18px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.16)"
                          : "0 0 18px rgba(91,148,144,0.28), 0 4px 12px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.12)",
                      transition: "all .2s cubic-bezier(.25,.46,.45,.94)",
                    }}
                  >
                    {isSubmitting ? "Sending…" : "Send Message"}
                    {!isSubmitting && <ArrowRight size={17} strokeWidth={2} />}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
